import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLazyRefreshMeQuery } from 'services/UsuariosApi';
import UserStorageService from 'services/UserStorageService';
import { authenticated, signOutSuccess } from 'store/slices/authSlice';
import { notifyUserDataUpdated } from './useCurrentUser';

// Estado global compartido para evitar múltiples instancias
let globalState = {
	isInitialized: false,
	isLoading: true,
	error: null,
	user: null,
};

let listeners = new Set();

const notifyListeners = () => {
	listeners.forEach(callback => callback(globalState));
};

const updateGlobalState = newState => {
	globalState = { ...globalState, ...newState };
	notifyListeners();
};

/**
 * Hook principal para manejar la inicialización de la aplicación
 * Verifica y actualiza los datos del usuario al cargar la app
 */
export const useAppInitialization = () => {
	const [localState, setLocalState] = useState(globalState);
	const dispatch = useDispatch();
	const [refreshMeTrigger] = useLazyRefreshMeQuery({
		refetchOnReconnect: false,
		refetchOnFocus: false,
	});

	// Suscribirse a cambios del estado global
	useEffect(() => {
		const callback = newState => {
			setLocalState(newState);
		};

		listeners.add(callback);

		return () => {
			listeners.delete(callback);
		};
	}, []);

	/**
	 * Actualiza el usuario en localStorage y state
	 */
	const updateUserData = useCallback(userData => {
		const userWithTimestamp =
			UserStorageService.markAsRecentlyFetched(userData);
		UserStorageService.saveUser(userWithTimestamp);

		updateGlobalState({ user: userWithTimestamp });
		notifyUserDataUpdated();
		return userWithTimestamp;
	}, []);

	/**
	 * Limpia datos de usuario y redirige al login
	 */
	const clearUserSession = useCallback(() => {
		UserStorageService.clearUser();
		dispatch(signOutSuccess());
		updateGlobalState({
			user: null,
			error: 'Sesión expirada',
		});
		notifyUserDataUpdated();
	}, [dispatch]);

	/**
	 * Refresca los datos del usuario desde el servidor
	 */
	const refreshUserData = useCallback(async () => {
		try {
			updateGlobalState({ error: null });
			const result = await refreshMeTrigger().unwrap();

			if (result) {
				const updatedUser = updateUserData(result);
				dispatch(authenticated(UserStorageService.getToken()));
				return updatedUser;
			} else {
				clearUserSession();
				return null;
			}
		} catch (error) {
			if (error.status === 401 || error.status === 403) {
				clearUserSession();
			} else {
				const localUser = UserStorageService.getUser();
				if (localUser) {
					updateGlobalState({
						user: localUser,
						error: 'No se pudieron actualizar los datos del usuario',
					});
				} else {
					clearUserSession();
				}
			}
			return null;
		}
	}, [refreshMeTrigger, updateUserData, clearUserSession, dispatch]);

	/**
	 * Inicializa la aplicación verificando el estado del usuario
	 */
	const initializeApp = useCallback(async () => {
		// Solo permitir una inicialización
		if (globalState.isInitialized && !globalState.isLoading) {
			return;
		}

		updateGlobalState({ isLoading: true, error: null });

		const hasToken = UserStorageService.hasValidToken();
		const localUser = UserStorageService.getUser();

		if (!hasToken) {
			updateGlobalState({
				isLoading: false,
				isInitialized: true,
			});
			return;
		}

		if (!localUser) {
			await refreshUserData();
		} else if (UserStorageService.isUserDataStale()) {
			await refreshUserData();
		} else {
			updateGlobalState({ user: localUser });
			dispatch(authenticated(UserStorageService.getToken()));
		}

		updateGlobalState({
			isLoading: false,
			isInitialized: true,
		});
	}, [refreshUserData, dispatch]);

	/**
	 * Fuerza una actualización de los datos del usuario
	 */
	const forceRefresh = useCallback(async () => {
		if (UserStorageService.hasValidToken()) {
			updateGlobalState({ isLoading: true });
			await refreshUserData();
			updateGlobalState({ isLoading: false });
		}
	}, [refreshUserData]);

	// Inicializar solo una vez
	useEffect(() => {
		initializeApp();
	}, [initializeApp]);

	return {
		isInitialized: localState.isInitialized,
		isLoading: localState.isLoading,
		error: localState.error,
		user: localState.user,
		forceRefresh,
		updateUserData,
		clearUserSession,

		// Helpers
		hasValidSession: !!localState.user && UserStorageService.hasValidToken(),
		needsConsent: localState.user && localState.user.needs_consent,
	};
};

export default useAppInitialization;
